import './Form.scss'

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import t from '../../helpers/i18n'
import useDelayedRun from '../../hooks/useDelayedRun'

const formEvent = ({ action, name, ...extraData }) => {
  return new CustomEvent('FormDataEvent', {
    bubbles: true,
    cancelable: true,
    detail: {
      action,
      name,
      ...extraData
    }
  })
}

const Form = ({ children, data, pending, onChange, onValidityChange }) => {
  const form = useRef()
  const requiredFields = useRef({})
  const [pendingFields] = useState(() => {
    if (!pending) return { current: {} }
    pending.current = {}
    return pending
  })

  useMemo(() => {
    if (!data) return
    if (!data.current) data.current = {}
  }, [data])

  const applyChange = useCallback(
    (valid) => {
      if (onValidityChange) onValidityChange(valid)
    },
    [onValidityChange]
  )

  const [setValid] = useDelayedRun(applyChange, 250, false)

  const triggerDataChange = useCallback(() => {
    if (onChange) onChange()
  }, [onChange])

  const checkRequiredFields = useCallback(() => {
    if (!data) return
    const invalid = Object.keys(requiredFields.current).find(
      (key) =>
        requiredFields.current[key] &&
        !pendingFields.current[key] &&
        (data.current[key] === null || data.current[key] === undefined)
    )
    setValid(!invalid)
  }, [requiredFields, pendingFields, data, setValid])

  const handleFormDataChange = useCallback((name, value) => {
    if (!data) return
    data.current[name] = value
    delete pendingFields.current[name]
    checkRequiredFields()
  }, [data, pendingFields, checkRequiredFields])

  const handleFormDataRequired = useCallback((name, required) => {
    requiredFields.current[name] = required
    checkRequiredFields()
  }, [requiredFields, checkRequiredFields])

  const handleFormDataRemove = useCallback((name) => {
    delete data.current[name]
    delete requiredFields.current[name]
    delete pendingFields.current[name]
    checkRequiredFields()
  }, [data, requiredFields, pendingFields, checkRequiredFields])

  const handleFormDataPending = useCallback((name, value) => {
    if (value && Array.isArray(value) && value[0] && value[0].get) {
      pendingFields.current[name] = value
      delete data.current[name]
    } else if (value && value.get) {
      pendingFields.current[name] = { value }
      delete data.current[name]
    } else if (value) {
      delete pendingFields.current[name]
      data.current[name] = value
    } else {
      delete pendingFields.current[name]
      delete data.current[name]
    }
    checkRequiredFields()
  }, [data, pendingFields, checkRequiredFields])

  useLayoutEffect(() => {
    const eventListener = (event) => {
      if (event.detail.action === 'UPDATE') {
        handleFormDataChange(event.detail.name, event.detail.value)
      } else if (event.detail.action === 'REQUIRED') {
        handleFormDataRequired(event.detail.name, event.detail.required)
      } else if (event.detail.action === 'REMOVE') {
        handleFormDataRemove(event.detail.name)
      } else if (event.detail.action === 'PENDING') {
        handleFormDataPending(event.detail.name, event.detail.value)
      } else {
        throw new Error(t('Event not supported: ') + event.detail.action)
      }
      triggerDataChange()
    }
    const element = form.current
    element.addEventListener('FormDataEvent', eventListener, false)
    return () => {
      element.removeEventListener('FormDataEvent', eventListener, false)
    }
  }, [
    form,
    handleFormDataChange,
    handleFormDataRequired,
    handleFormDataRemove,
    handleFormDataPending,
    triggerDataChange
  ])

  return (
    <div ref={form} className='form'>
      {children}
    </div>
  )
}

const FormBand = ({text}) => {
  return (
    <div className="-form-band">
      <div className="-number label-small-medium" />
      <div className="-text overline-label-large-medium">{text}</div>
    </div>
  )
}

export { Form, FormBand, formEvent }
